import { useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import BpmnViewer from "bpmn-js/lib/Viewer";
import NavigationModule from "diagram-js/lib/navigation/movecanvas";
import "bpmn-js/dist/assets/bpmn-font/css/bpmn-embedded.css";
import "bpmn-js/dist/assets/bpmn-js.css";
import "bpmn-js/dist/assets/diagram-js.css";

export type ProcessBpmnViewerProps = Readonly<{
  bpmnXML: string;
  height: string;
}>;

/**
 * BPMN read-only viewer scoped to a local container ref.
 * Avoids the global `#canvas` id used by @freyr/freyr-react-common-components BPMNViewer.
 */
export function ProcessBpmnViewer({ bpmnXML, height }: ProcessBpmnViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !bpmnXML) return;

    const viewer = new BpmnViewer({
      container,
      height,
      additionalModules: [NavigationModule],
    });

    let cancelled = false;
    const fitDiagram = () => {
      if (cancelled) return;
      const canvas = viewer.get("canvas") as {
        zoom: (level: unknown, center?: unknown) => void;
      };
      // Without `center`, diagram-js pins small diagrams to the top-left.
      canvas.zoom("fit-viewport", "auto");
    };

    void viewer
      .importXML(bpmnXML)
      .then(() => {
        fitDiagram();
        requestAnimationFrame(fitDiagram);
      })
      .catch((err: unknown) => {
        console.error("Failed to import BPMN diagram:", err);
      });

    return () => {
      cancelled = true;
      viewer.destroy();
    };
  }, [bpmnXML, height]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        minHeight: height,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& .bjs-container": {
          width: "100% !important",
        },
      }}
    />
  );
}
